import { motion } from "framer-motion";
import FaqList from "@/components/site/FaqList";
import { reveal } from "@/components/site/motionPresets";

/**
 * ⚠️ These questions and answers are mirrored in the FAQPage node of the
 * JSON-LD graph in `public/index.html`. Schema that does not match visible
 * content is a structured-data violation and answer engines cross-check it,
 * so if you edit one you must edit the other.
 *
 * Each answer leads with a self-contained sentence. Answer engines extract
 * definitional leads, and a sentence that only parses with surrounding context
 * does not get quoted.
 */
export const FAQ_ITEMS = [
  {
    term: "What is OneVidaria Digital?",
    detail:
      "OneVidaria Digital is an independent software studio in Las Piñas, Philippines, that builds consumer apps, data tools, and Android utilities. Most of its products sit in two named lines: the -aria line, which people use directly, and the -Pulse line, which filters and interprets data. The rest stand on their own: Agos, a keyboard, Pocket Peachy, a chibi action-fighter, and Evi, a location observation prototype. Ten products exist: Daubaria and Vidaria are in public testing, Agos is invitation-only, SievePulse is internal, Pocket Peachy is coming soon, Evi has not been released, and four are in development.",
  },
  {
    term: "What products does OneVidaria Digital make?",
    detail:
      "OneVidaria Digital makes ten products: Daubaria, Vidaria, Peraria, ScorePulse, PixelPulse, SievePulse, StrataPulse, Agos, Pocket Peachy, and Evi. Daubaria is a real-time multiplayer card-daubing game and Vidaria is a botanical community database. The -Pulse products cover live event scoring, device-native media editing, road-closure filtering, and Philippine hazard telemetry. Agos is an Android keyboard for Roman and Traditional Baybayin typing, Pocket Peachy is a fictional chibi action-fighter, and Evi is an Android prototype for recording location observations.",
  },
  {
    term: "Which OneVidaria products can I use today?",
    detail:
      "Daubaria is in open testing on Google Play and Vidaria is in alpha on the web. Agos is available through invitation-only internal testing. SievePulse is an internal OneVidaria Digital tool and is not publicly available. Pocket Peachy is coming soon to Android, Evi is an unreleased prototype, and Peraria, ScorePulse, PixelPulse, and StrataPulse remain in development without announced launch dates.",
  },
  {
    term: "Is Daubaria gambling?",
    detail:
      "No. Daubaria is a skill-based card-daubing game made for entertainment, with no real-money bets, no cash betting, and no payouts of any kind. It is 18+ amusement only, and attempting to use the platform for gambling results in immediate account termination.",
  },
  {
    term: "Where is OneVidaria Digital based?",
    detail:
      "OneVidaria Digital is based in Las Piñas City, Metro Manila, Philippines. SievePulse and StrataPulse are built specifically around Philippine road and hazard data, while the consumer apps are usable anywhere.",
  },
  {
    term: "Who founded OneVidaria Digital?",
    detail:
      "OneVidaria Digital was founded in 2026 by Enrique Olorvida. The studio came out of years spent managing online communities and running into the same broken tools, and it exists to build the versions that should have existed.",
  },
];

// The five services questions deliberately live on /services rather than here.
// This FAQ renders on the homepage, and the homepage carries no pricing — see
// servicesContent.jsx → SERVICE_FAQ.

export default function Faq() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      aria-label="Common questions about OneVidaria Digital"
      className="relative pt-28 sm:pt-36 pb-12 sm:pb-16"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.h2
          {...reveal({ y: 18, duration: 0.8, margin: undefined })}
          data-testid="faq-heading"
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-white"
        >
          Common questions.
        </motion.h2>

        <div className="mt-10 max-w-3xl">
          <FaqList items={FAQ_ITEMS} />
        </div>
      </div>
    </section>
  );
}
